// Payment dialog (approved #4: the #3 order with this dialog open).
//
// W36 (2026-10-20): `mode="real"` renders the same shell and the design's
// labels verbatim for a real HubSpot session, with every entry control
// disabled and labelled "Card entry opens when provider-backed payment is
// enabled". There is no saved card to pick (the W32 "No saved card on
// file in this review" state), the "4242" example never renders, the
// customer confirmations stay disabled because the final amount is
// pending Shopify tax, and "Prepare secure payment link" is live only
// when LIVE_WRITE_GATE_PAYMENT_PREPARE_SECURE_LINK is ON (otherwise
// inert with the gate label). Preview output is unchanged.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../_shared/IconSprite.js';
import { formatCents } from '../../../data/livePricing.js';
import {
  emptyAgreement,
  paymentAgreementKey,
  type AppliedPayment,
  type SampleCardDescriptor,
} from '../orderState.js';
import type { GateState } from '../enginePricing.js';

export const CARD_ENTRY_DISABLED_LABEL = 'Card entry opens when provider-backed payment is enabled';
export const NO_SAVED_CARD_DIALOG_LABEL = 'No saved card on file in this review';
export const SECURE_LINK_GATE_LABEL = 'Paused by the live-write gate';

interface CardEntry {
  name: string;
  number: string;
  expiry: string;
  cvv: string;
  billingSame: boolean;
  billingName: string;
  street: string;
  line2: string;
  city: string;
  region: string;
  postal: string;
  country: string;
}

const blankEntry = (billingSame = true): CardEntry => ({
  name: '', number: '', expiry: '', cvv: '', billingSame,
  billingName: '', street: '', line2: '', city: '', region: '', postal: '', country: '',
});

function clearRawInputs(form: HTMLFormElement | null): void {
  form?.reset();
  form?.querySelectorAll<HTMLInputElement>('input[type="text"], input[type="password"]').forEach((input) => {
    input.value = '';
    input.defaultValue = '';
  });
}

export interface SecureLinkControl {
  gate: GateState;
  gateName: string;
  onPrepare: () => void;
}

export interface PaymentDialogProps {
  applied: AppliedPayment;
  orderContextKey: string;
  totalCents: number | null;
  subscription: boolean;
  frequencyDays: string;
  hasShippingAddress: boolean;
  onApply: (selection: AppliedPayment) => void;
  onCancel: () => void;
  onPrepareLink: () => void;
  /** 'preview' is the approved design with the example card; 'real' is the W36 shell. */
  mode?: 'preview' | 'real';
  /** Real first name for the copy; defaults to the design-preview customer. */
  customerFirstName?: string;
  /** Real-session secure-link gate; undefined keeps the preview behaviour. */
  secureLink?: SecureLinkControl;
}

export function PaymentDialog(props: PaymentDialogProps): ReactNode {
  const { applied, orderContextKey, totalCents, subscription, frequencyDays, hasShippingAddress,
    onApply, onCancel, onPrepareLink, mode = 'preview', customerFirstName = 'Sarah', secureLink } = props;
  const real = mode === 'real';
  const firstName = customerFirstName;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const revision = useRef(applied.sample?.revision ?? 0);
  const [method, setMethod] = useState(applied.method);
  // Raw entry belongs only to this mounted dialog; callbacks receive only the
  // four-field masked descriptor. No raw values enter shared draft storage.
  const [entry, setEntry] = useState(() => blankEntry(applied.sample?.billingSame ?? true));
  const [sample, setSample] = useState<SampleCardDescriptor | null>(applied.sample);
  const [agreement, setAgreement] = useState(applied.agreement);
  const sampleReady = !real && !!sample?.complete && (!sample.billingSame || hasShippingAddress);
  const effectiveSample = sample && !sampleReady ? { ...sample, complete: false } : sample;
  const agreementKey = paymentAgreementKey(orderContextKey, method, effectiveSample);
  const amountKnown = totalCents !== null && Number.isSafeInteger(totalCents) && totalCents >= 0;
  const canConfirm = !real && amountKnown && (method === 'saved' || sampleReady);
  const paymentConfirmed = canConfirm && agreement.key === agreementKey && agreement.payment;
  const recurringConfirmed = canConfirm && subscription && agreement.key === agreementKey && agreement.recurring;
  const linkEnabled = !real || secureLink?.gate === 'on';

  useEffect(() => {
    const dialog = dialogRef.current;
    const form = formRef.current;
    const previousFocus = document.activeElement as HTMLElement | null;
    if (dialog && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
    }
    return () => {
      clearRawInputs(form);
      previousFocus?.focus();
    };
  }, []);

  useEffect(() => {
    setAgreement((current) => current.key === agreementKey ? current : emptyAgreement(agreementKey));
  }, [agreementKey]);

  function clearEntry(): void {
    clearRawInputs(formRef.current);
    setEntry(blankEntry());
  }

  function cancel(): void {
    clearEntry();
    onCancel();
  }

  function selectMethod(next: AppliedPayment['method']): void {
    if (next === method) return;
    clearEntry();
    setSample(null);
    setMethod(next);
    setAgreement(emptyAgreement());
  }

  function changeEntry<K extends keyof CardEntry>(key: K, value: CardEntry[K]): void {
    if (real) return;
    const next = { ...entry, [key]: value };
    setEntry(next);
    const input = next.number.trim();
    const digits = input.replace(/[ -]/g, '');
    const numberOkay = /^[0-9 -]+$/.test(input) && /^[0-9]{12,19}$/.test(digits);
    const billingOkay = next.billingSame
      ? hasShippingAddress
      : [next.billingName, next.street, next.city, next.region, next.postal, next.country].every((v) => v.trim());
    const complete = !!(next.name.trim() && numberOkay
      && /^(0[1-9]|1[0-2])\s*\/\s*(?:[0-9]{2}|[0-9]{4})$/.test(next.expiry.trim())
      && /^[0-9]{3,4}$/.test(next.cvv.trim()) && billingOkay);
    revision.current += 1;
    setSample({ revision: revision.current, last4: numberOkay ? digits.slice(-4) : '', complete, billingSame: next.billingSame });
    setAgreement(emptyAgreement());
  }

  function apply(): void {
    if (real) return;
    if (method === 'new' && !sampleReady) return;
    const selection: AppliedPayment = {
      method,
      sample: method === 'new' && sample
        ? { revision: sample.revision, last4: sample.last4, complete: sample.complete, billingSame: sample.billingSame }
        : null,
      agreement: {
        key: agreementKey,
        payment: paymentConfirmed,
        recurring: subscription && recurringConfirmed,
      },
    };
    clearEntry();
    onApply(selection);
  }

  function prepareLink(): void {
    if (!linkEnabled) return;
    clearEntry();
    if (real && secureLink) secureLink.onPrepare();
    else onPrepareLink();
  }

  const field = (key: Exclude<keyof CardEntry, 'billingSame'>, label: string, options: {
    type?: string; placeholder?: string; numeric?: boolean; wide?: boolean; maxLength?: number;
  } = {}): ReactNode => (
    <label className={'entry-field' + (options.wide ? ' entry-wide' : '')}>
      {label}
      <input
        type={options.type ?? 'text'}
        value={entry[key]}
        autoComplete="off"
        spellCheck={false}
        inputMode={options.numeric ? 'numeric' : undefined}
        placeholder={options.placeholder}
        maxLength={options.maxLength ?? 120}
        disabled={real}
        aria-describedby={real ? 'order-card-entry-note' : undefined}
        onChange={(event) => changeEntry(key, event.target.value)}
      />
    </label>
  );

  const confirmScript = (): string => {
    if (real) return CARD_ENTRY_DISABLED_LABEL + '.';
    if (method === 'new' && !sampleReady) return 'Enter sample card details above to review the customer confirmation.';
    return '“Would you like to use the card ending in ' + (method === 'saved' ? '4242' : sample?.last4) + ' for this order?”';
  };

  const permissionStatus = (): string => {
    if (!amountKnown) return 'Final amount awaits tax from Shopify.';
    if (!canConfirm) return 'Complete the sample card details and billing address above to review customer permissions.';
    if (paymentConfirmed && (!subscription || recurringConfirmed)) return 'Use this payment method to keep these customer permissions.';
    return 'Confirm the card and amount with ' + firstName + (subscription ? ', including the subscription schedule and recurring terms.' : '.');
  };

  const linkGateNote = (): string => {
    if (!real) return 'No card is submitted or verified in this design preview.';
    if (secureLink?.gate === 'on') return 'The payment provider link is prepared through the runtime; nothing is charged from this screen.';
    if (secureLink?.gate === 'loading') return `${SECURE_LINK_GATE_LABEL} (${secureLink.gateName}). Checking the gate…`;
    return `${SECURE_LINK_GATE_LABEL} (${secureLink?.gateName ?? 'LIVE_WRITE_GATE_PAYMENT_PREPARE_SECURE_LINK'}). No payment link is created or sent.`;
  };

  return (
    <dialog
      ref={dialogRef}
      id="order-payment-dialog"
      className="order-dialog order-payment-dialog"
      aria-labelledby="order-payment-dialog-title"
      data-mode={mode}
      onCancel={(event) => { event.preventDefault(); cancel(); }}
      onClose={cancel}
    >
      <h2 id="order-payment-dialog-title">Choose a payment method</h2>
      <form ref={formRef} onSubmit={(event) => { event.preventDefault(); apply(); }}>
        <div className="payment-body">
          <p className="payment-intro">Confirm which card {firstName} wants to use.</p>
          {real ? (
            <div className="saved-card is-selected" data-testid="payment-no-saved-card" role="group" aria-label="Saved payment card">
              <span className="visa">CARD</span>
              <span><strong>{NO_SAVED_CARD_DIALOG_LABEL}</strong>
                <span className="card-holder">{firstName} · No card on file</span>
                <span className="saved-label">Saved cards appear here once the payment provider is connected.</span></span>
              <span className="selection-dot" />
            </div>
          ) : (
            <button className={'saved-card' + (method === 'saved' ? ' is-selected' : '')} type="button"
              aria-pressed={method === 'saved'} onClick={() => selectMethod('saved')}>
              <span className="visa">VISA</span>
              <span><strong>Visa ending in 4242</strong>
                <span className="card-holder">Sarah Mitchell · Expires 08/2028</span>
                <span className="saved-label">Saved with payment provider · Example</span></span>
              <span className="selection-dot" />
            </button>
          )}
          <div className="payment-notice"><Icon id="check" /><p>For the saved card above, the full number is held by the payment provider. Only masked details appear here.</p></div>
          <div className="eligibility"><span className="status-amber">Verification needed</span>
            <p>A saved card must be eligible for this specific payment. Prior order history alone does not authorize a charge.</p></div>
          <button className="new-card-button" type="button" aria-pressed={method === 'new'}
            aria-controls="order-payment-new-panel" aria-expanded={method === 'new'} onClick={() => selectMethod('new')}>
            Use a different or new card <Icon id="arrow" />
          </button>
          <div id="order-payment-new-panel" hidden={method !== 'new'}>
            <h3>Enter a new card</h3>
            <p id="order-card-entry-note" className="entry-preview-note">
              {real ? CARD_ENTRY_DISABLED_LABEL + '.' : 'Design preview · Use sample card details only.'}
            </p>
            <div className="card-entry-grid" aria-describedby="order-card-entry-note">
              {field('name', 'Name on card', { wide: true, placeholder: 'Name as shown on card', maxLength: 80 })}
              {field('number', 'Card number', { wide: true, numeric: true, placeholder: '0000 0000 0000 0000', maxLength: 23 })}
              {field('expiry', 'Expiration', { numeric: true, placeholder: 'MM/YY', maxLength: 7 })}
              {field('cvv', 'Security code', { type: 'password', numeric: true, placeholder: 'CVV', maxLength: 4 })}
              <label className="entry-billing-same entry-wide">
                <input type="checkbox" checked={entry.billingSame} aria-controls="order-card-billing-fields" disabled={real}
                  aria-expanded={!entry.billingSame} onChange={(event) => changeEntry('billingSame', event.target.checked)} />
                Billing address is the same as shipping
              </label>
              <p className="entry-billing-note entry-wide" hidden={!entry.billingSame}>Uses this order’s shipping address.</p>
              <fieldset id="order-card-billing-fields" className="entry-wide" hidden={entry.billingSame} disabled={entry.billingSame || real}>
                <legend>Billing address</legend>
                <div className="card-entry-grid billing-entry-grid">
                  {field('billingName', 'Full name', { wide: true })}
                  {field('street', 'Address line 1', { wide: true })}
                  {field('line2', 'Address line 2 (optional)', { wide: true })}
                  {field('city', 'City')}
                  {field('region', 'State / province')}
                  {field('postal', 'ZIP / postal code')}
                  {field('country', 'Country')}
                </div>
              </fieldset>
            </div>
            <p id="order-card-entry-status" className="entry-status" role="status">
              {real
                ? CARD_ENTRY_DISABLED_LABEL + '. Entry fields stay disabled on this session.'
                : sampleReady
                  ? 'Sample card ending in ' + sample!.last4 + ' · Design preview only. Entry fields clear when this popup closes.'
                  : 'Complete the sample card details and billing address to review this payment method.'}
            </p>
            <div className="entry-link-alternative">
            <p>Or let {firstName} enter {real ? 'a' : 'her'} card through a secure payment link.</p>
            <button className="payment-blue" type="button" onClick={prepareLink} disabled={!linkEnabled}
              aria-describedby={real ? 'order-payment-link-gate' : undefined}>
              Prepare secure payment link
            </button>
            <small id="order-payment-link-gate" data-testid="order-payment-link-gate">{linkGateNote()}</small>
            </div>
          </div>
          <section className="payment-permissions" aria-labelledby="order-payment-permissions-title">
            <h3 id="order-payment-permissions-title">Confirm with {firstName}</h3>
            <p id="order-payment-permission-summary" className="permission-summary">
              {amountKnown ? 'Current order amount: ' + formatCents(totalCents!) : 'Final amount awaits tax from Shopify.'}
              {' · '}{subscription ? 'Subscription every ' + frequencyDays + ' days' : 'One-time order'}
            </p>
            <div className="confirm-script">{confirmScript()}</div>
            <label className="customer-confirm"><input type="checkbox" disabled={!canConfirm} checked={paymentConfirmed}
              onChange={(event) => setAgreement((current) => ({ ...current, key: agreementKey, payment: event.target.checked }))} />
              Customer confirmed the selected card and the final order amount.
            </label>
            {subscription ? <label className="customer-confirm"><input type="checkbox" disabled={!canConfirm} checked={recurringConfirmed}
              onChange={(event) => setAgreement((current) => ({ ...current, key: agreementKey, recurring: event.target.checked }))} />
              Customer agreed to the subscription schedule and recurring terms.
            </label> : null}
            <p className="permission-status" role="status">{permissionStatus()}</p>
            <p className="source-note">Retain the source conversation and the exact terms confirmed. A confirmation checkbox does not replace the underlying authorization checks.</p>
          </section>
        </div>
        <footer>
          <button type="button" onClick={cancel}>Cancel</button>
          <button type="submit" className="confirm" disabled={real || (method === 'new' && !sampleReady)}>Use this payment method</button>
        </footer>
      </form>
    </dialog>
  );
}
